import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import FilterPanel from "@/components/feature/FilterPanel";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { getCategoryById, getMoviesByCategory, applyMovieFilter, defaultFilterState } from "@/utils/movie";
import type { MovieFilterState } from "@/utils/movie";
import { PAGE_SIZE } from "@/mocks/filters";
import { useSeo } from "@/hooks/useSeo";

export default function CategoryPage() {
  const { categoryId = "" } = useParams();
  const category = getCategoryById(categoryId);
  const [filter, setFilter] = useState<MovieFilterState>(defaultFilterState);
  const [page, setPage] = useState(1);

  useSeo({
    title: category ? `${category.name} - 分类列表 - 韩国三级电影` : "分类列表 - 韩国三级电影",
    description: category
      ? `${category.description} 精选韩国三级电影${category.name}合集，支持按类型、年份与评分筛选排序，快速查找感兴趣的作品。`
      : "韩国三级电影分类列表，支持按类型、年份与评分筛选查找作品。",
    keywords: `韩国三级电影,${category ? category.name : "分类列表"},韩国电影,电影资源,剧情介绍`,
  });

  const baseList = useMemo(() => (category ? getMoviesByCategory(categoryId) : []), [category, categoryId]);
  const filtered = useMemo(() => applyMovieFilter(baseList, filter), [baseList, filter]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleFilterChange = (next: MovieFilterState) => {
    setFilter(next);
    setPage(1);
  };

  const handleReset = () => {
    setFilter(defaultFilterState);
    setPage(1);
  };

  if (!category) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
          <EmptyState
            icon="ri-folder-warning-line"
            title="未找到该分类"
            description="你访问的分类可能已被移除或链接有误，可返回首页浏览全部内容。"
            action={
              <Link
                to="/"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
              >
                返回首页
              </Link>
            }
          />
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: category.name }]} />

        <header className="mt-4 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:flex-row md:items-center md:justify-between md:p-6">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <i className={`${category.icon} text-2xl`} aria-hidden="true" />
            </span>
            <div>
              <h1 className="text-xl font-black text-foreground-950 md:text-2xl">{category.name}</h1>
              <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {category.description}
              </p>
            </div>
          </div>
          <span className="shrink-0 whitespace-nowrap rounded-md bg-secondary-100 px-3 py-1.5 text-sm font-medium text-secondary-900">
            共 {filtered.length} 部作品
          </span>
        </header>

        <div className="mt-5">
          <FilterPanel value={filter} onChange={handleFilterChange} />
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-4 xl:grid-cols-5">
              {pageItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <div className="mt-8">
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </div>
          </>
        ) : (
          <div className="mt-6">
            <EmptyState
              icon="ri-search-eye-line"
              title="没有符合条件的作品"
              description="当前的筛选组合没有匹配到影片，试试放宽条件或重置筛选。"
              action={
                <button
                  type="button"
                  onClick={handleReset}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
                >
                  <i className="ri-refresh-line text-base" aria-hidden="true" />
                  重置筛选
                </button>
              }
            />
          </div>
        )}
      </div>
    </SiteLayout>
  );
}